import { useState, useEffect } from 'react';
import { X, Link as LinkIcon, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface FundCall {
  id: string;
  name: string;
  subsidy_id: string;
  subsidy_name: string;
  requested_amount: number;
  call_date: string;
}

interface AddFundCallLinkModalProps {
  invoiceId: string;
  invoiceSupplier: string;
  remainingAmount: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddFundCallLinkModal({
  invoiceId,
  invoiceSupplier,
  remainingAmount,
  onClose,
  onSuccess
}: AddFundCallLinkModalProps) {
  const [fundCalls, setFundCalls] = useState<FundCall[]>([]);
  const [filteredFundCalls, setFilteredFundCalls] = useState<FundCall[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFundCallId, setSelectedFundCallId] = useState('');
  const [allocatedAmount, setAllocatedAmount] = useState(remainingAmount > 0 ? remainingAmount.toString() : '');
  const [justificationNote, setJustificationNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadFundCalls();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = fundCalls.filter(fc =>
        fc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fc.subsidy_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredFundCalls(filtered);
    } else {
      setFilteredFundCalls(fundCalls);
    }
  }, [searchTerm, fundCalls]);

  const loadFundCalls = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get already linked fund calls
      const { data: linkedFundCalls } = await supabase
        .from('grant_fund_call_invoices')
        .select('fund_call_id')
        .eq('invoice_id', invoiceId);

      const linkedIds = linkedFundCalls?.map(lfc => lfc.fund_call_id) || [];

      // Get all fund calls not already linked
      const { data, error } = await supabase
        .from('grant_fund_calls')
        .select(`
          id,
          name,
          requested_amount,
          call_date,
          subsidy_id,
          subsidy:grant_subsidies(name)
        `)
        .eq('user_id', user.id)
        .order('call_date', { ascending: false });

      if (error) throw error;

      const fundCallsData = (data || [])
        .filter((fc: any) => !linkedIds.includes(fc.id))
        .map((fc: any) => ({
          id: fc.id,
          name: fc.name,
          subsidy_id: fc.subsidy_id,
          subsidy_name: fc.subsidy.name,
          requested_amount: fc.requested_amount,
          call_date: fc.call_date,
        }));

      setFundCalls(fundCallsData);
      setFilteredFundCalls(fundCallsData);
    } catch (error) {
      console.error('Error loading fund calls:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedFundCallId) {
      setError('Veuillez sélectionner un ADF');
      return;
    }

    if (!allocatedAmount || parseFloat(allocatedAmount) <= 0) {
      setError('Le montant affecté doit être supérieur à 0');
      return;
    }

    setSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      const { error: linkError } = await supabase
        .from('grant_fund_call_invoices')
        .insert({
          user_id: user.id,
          fund_call_id: selectedFundCallId,
          invoice_id: invoiceId,
          allocated_amount: parseFloat(allocatedAmount),
          justification_note: justificationNote.trim() || null,
        });

      if (linkError) throw linkError;

      onSuccess();
    } catch (err: any) {
      console.error('Error linking fund call:', err);
      setError(err.message || 'Erreur lors de la liaison');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const selectedFundCall = fundCalls.find(fc => fc.id === selectedFundCallId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Lier à un ADF</h2>
            <p className="text-sm text-gray-600 mt-1">Facture: {invoiceSupplier}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          {remainingAmount > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
              Reste à couvrir sur cette facture: <strong>{formatCurrency(remainingAmount)}</strong>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un ADF..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* Fund calls list */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
            </div>
          ) : filteredFundCalls.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              {searchTerm ? 'Aucun ADF trouvé' : 'Aucun ADF disponible'}
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredFundCalls.map((fundCall) => (
                <div
                  key={fundCall.id}
                  onClick={() => setSelectedFundCallId(fundCall.id)}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedFundCallId === fundCall.id
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-emerald-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{fundCall.subsidy_name}</div>
                      <div className="text-sm text-gray-600">{fundCall.name}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        {formatDate(fundCall.call_date)} • Demandé: {formatCurrency(fundCall.requested_amount)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Link form */}
          {selectedFundCall && (
            <form onSubmit={handleSubmit} className="space-y-4 border-t border-gray-200 pt-6">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="text-sm font-medium text-emerald-900 mb-2">ADF sélectionné</div>
                <div className="text-sm text-emerald-800">
                  {selectedFundCall.subsidy_name} - {selectedFundCall.name}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant affecté de cet ADF (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={allocatedAmount}
                  onChange={(e) => setAllocatedAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Montant de cette facture financé par cet ADF
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note de justification
                </label>
                <textarea
                  value={justificationNote}
                  onChange={(e) => setJustificationNote(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Ex: Acompte 30%, Solde, etc."
                />
              </div>
            </form>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={submitting}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedFundCallId || submitting}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <LinkIcon className="w-5 h-5" />
            {submitting ? 'Liaison...' : 'Lier l\'ADF'}
          </button>
        </div>
      </div>
    </div>
  );
}
